import React, { useState, useEffect } from 'react';
import axios from 'axios';

const LiveMaps = () => {
  const [availableSystems, setAvailableSystems] = useState([]);
  const [selectedSystem, setSelectedSystem] = useState('');
  const [availablePlots, setAvailablePlots] = useState([]);
  const [selectedPlot, setSelectedPlot] = useState('');
  const [isLoadingSystems, setIsLoadingSystems] = useState(false);
  const [isLoadingPlots, setIsLoadingPlots] = useState(false);
  const [userId, setUserId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [modalMessage, setModalMessage] = useState('');
  const [showErrorModal, setShowErrorModal] = useState(false);

  // Fetch user ID on component mount
  useEffect(() => {
    const getUserId = async () => {
      try {
        const token = localStorage.getItem('access');
        if (!token) throw new Error('No token found. Please log in again.');
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/auth/users/me/`, {
          headers: { Authorization: `JWT ${token}`, 'Content-Type': 'application/json' },
        });
        // console.log('User ID:', response.data.id);
        setUserId(response.data.id);
      } catch (error) {
        console.error('Error fetching user ID:', error);
      }
    };
    getUserId();
  }, []);  

  // Fetch systems on component mount
  useEffect(() => {
    const fetchSystems = async () => {
      if (userId) {
        setIsLoadingSystems(true);
        try {
          const response = await axios.get(
            `https://r6zvszx9u8.execute-api.us-west-2.amazonaws.com/dev4/fetchSystems`,
            { params: { userId } }
          );
  
          // console.log("Raw response from backend:", response);
          if (response.data && Array.isArray(response.data.systems)) {
            setAvailableSystems(response.data.systems);
          } else {
            console.error("Error: systems data not found or invalid in response.");
            setAvailableSystems([]);
            setModalMessage("Invalid response received. Please try again later.");
            setShowErrorModal(true);
          }
        } catch (error) {
          console.error("Error fetching systems:", error);
          setAvailableSystems([]);
          setModalMessage("Sorry, we cannot fetch your systems at this time. Please try again later.");
          setShowErrorModal(true);
        } finally {
          setIsLoadingSystems(false);
        }
      }
    };
    fetchSystems();
  }, [userId]);
  
  // Log the updated availableSystems
  useEffect(() => {
    // console.log("Updated availableSystems:", availableSystems);
  }, [availableSystems]);

  const handleSystemChange = (event) => {
    const system = event.target.value;
    setSelectedSystem(system);
  
    if (system) {
      // Timestamp to refresh and avoid caching
      const timestamp = new Date().getTime();
      const plotUrl = `https://live-maps-bucket.s3.us-west-2.amazonaws.com/plots/user_${userId}/${system}/${system}.png?v=${timestamp}`;;
      
      setAvailablePlots([{ name: system, url: plotUrl }]); // Auto-set the plot
      setSelectedPlot(plotUrl); // Preselect the plot
    } else {
      setAvailablePlots([]);
      setSelectedPlot('');
    }
  };  

  // Handle plot selection
  const handlePlotChange = (event) => {
    setSelectedPlot(event.target.value);
  };

  // Open modal to show the selected plot
  const openModal = () => {
    const selectedPlotObject = availablePlots.find((plot) => plot.url === selectedPlot);
    if (selectedPlotObject) {
      setModalMessage(
        `<img src="${selectedPlotObject.url}" alt="${selectedPlotObject.name}" style="width: 100%; height: auto; max-height: 95vh;" />`
      );
      setIsModalOpen(true);
    } else {
      console.error('Selected plot not found in availablePlots.');
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const isButtonDisabled = !selectedSystem || !selectedPlot;

  return (
    <div style={{ padding: '20px', textAlign: 'center' }}>
      <h1 style={{ fontSize: '36px', fontWeight: '500', marginBottom: '30px' }}>Live Moisture Maps</h1>
      <h6 style={{ marginBottom: '20px' }}>Select a System to view its corresponding live map </h6>

      {/* Systems Dropdown */}
      <div style={{ marginBottom: '30px', width: '400px', margin: 'auto', marginTop: '20px' }}>
        <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px', textAlign: 'left' }}>Select System:</label>
        <select
          value={selectedSystem}
          onChange={handleSystemChange}
          style={{ width: '400px', padding: '8px', fontSize: '16px', borderRadius: '5px' }}
          disabled={isLoadingSystems || availableSystems.length === 0}
        >
          <option value="">Select System</option>
          {isLoadingSystems ? (
            <option>Loading systems...</option>
          ) : (
            availableSystems.map((system) => (
              <option key={system} value={system}>
                {system}
              </option>
            ))
          )}
        </select>
      </div>

      {/* Plots Dropdown */}
      {selectedSystem && (
        <div style={{ marginBottom: '30px', width: '400px', margin: 'auto', marginTop: '20px' }}>
          <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px', textAlign: 'left' }}>
            Live Map:
          </label>
          <select
            value={selectedPlot}
            onChange={handlePlotChange}
            style={{ width: '400px', padding: '8px', fontSize: '16px', borderRadius: '5px' }}
            disabled={isLoadingPlots || availablePlots.length === 0}
          >
            {availablePlots.map((plot, index) => (
              <option key={index} value={plot.url}>
                {plot.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Generate Button */}
      <button
        onClick={openModal}
        disabled={isButtonDisabled}
        style={{
          backgroundColor: isButtonDisabled ? '#A9A9A9' : '#4CAF50',
          color: 'white',
          marginTop: '30px',
          padding: '14px 28px',
          fontSize: '18px',
          borderRadius: '5px',
          cursor: isButtonDisabled ? 'not-allowed' : 'pointer',
          transition: 'background-color 0.3s ease', // Smooth transition for hover
        }}
        onMouseEnter={(e) => {
          if (!isButtonDisabled) {
            e.target.style.backgroundColor = '#45a049'; // Darker green on hover
          }
        }}
        onMouseLeave={(e) => {
          if (!isButtonDisabled) {
            e.target.style.backgroundColor = '#4CAF50'; // Reset to original color
          }
        }}
      >
        View Live Map
      </button>

      {/* Modal */}
      {isModalOpen && (
        <div
          className="modal"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <div
            style={{
              backgroundColor: '#fff',
              padding: '20px', // Increased padding for better spacing
              borderRadius: '8px',
              width: '90%', // Adjust width to be more mobile-friendly
              maxWidth: '390px', // Still use a max-width for larger screens
              maxHeight: '90vh', // Limit height to avoid overflowing on smaller screens
              overflow: 'auto',
              textAlign: 'center',
              position: 'relative',
            }}
          >
            <button
              onClick={closeModal}
              style={{
                position: 'absolute',
                top: '5px',
                right: '5px',
                backgroundColor: 'red',
                color: 'white',
                border: 'none',
                padding: '8px 12px',
                cursor: 'pointer',
              }}
            >
              X
            </button>
            <p
              style={{
                textAlign: 'center',
                color: 'black',
                marginTop: '20px',
                fontWeight: 'bold',
              }}
            >
              Right-click to "Save image as..." or hold to save image on mobile device
            </p>
            <div dangerouslySetInnerHTML={{ __html: modalMessage }} />
          </div>
        </div>
      )}

     {showErrorModal && (
        <div style={{
          position: "fixed", top: 0, left: 0, width: "100%", height: "100%",
          background: "rgba(0, 0, 0, 0.5)", display: "flex", justifyContent: "center",
          alignItems: "center", zIndex: 9999
        }}>
          <div style={{
            background: "white", padding: "20px", borderRadius: "8px",
            boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)", textAlign: "center", position: "relative"
          }}>
            <button
              onClick={() => setShowErrorModal(false)}
              style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                backgroundColor: 'red',
                color: 'white',
                border: 'none',
                padding: '5px 10px',
                cursor: 'pointer',
                fontSize: '16px',
                width: '30px',
                height: '30px',
                lineHeight: '0',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              X
            </button>
            <h2 style={{ color: 'red' }}>Error</h2>
            <p>{modalMessage}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveMaps;